import type { DataLevel } from '../lib/securityFilter'
import { levelRoleName } from '../lib/securityFilter'
import { getPermissionApplyUrl } from '../lib/gatekeeperUrls'
import { SecurityBadge } from './SecurityBadge'

interface SecurityBlockProps {
  level: DataLevel
  /** 当前用户数据等级（1=viewer 2=optimizer 3=lead 4=admin），取 adContext.data_level */
  userLevel: number
  title?: string
  /** compact：内联条样式（等级色背景，适合卡片内部）；默认 block：大区块居中样式 */
  variant?: 'block' | 'compact'
  children: React.ReactNode
}

// 等级对应的内联条配色（与设计文档对齐）
const levelTheme: Record<DataLevel, { bg: string; text: string; border: string }> = {
  L1: { bg: '#f0fdf4', text: '#16a34a', border: '#bbf7d0' },
  L2: { bg: '#E6F1FB', text: '#0C447C', border: '#85B7EB' },
  L3: { bg: '#FFFBEB', text: '#BA7517', border: '#FAC775' },
  L4: { bg: '#FEF2F2', text: '#A32D2D', border: '#FECACA' },
}

function LockIcon({ size = 16, color = 'currentColor' }: { size?: number; color?: string }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" style={{ flexShrink: 0 }}>
      <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
      <path d="M7 11V7a5 5 0 0 1 10 0v4" />
    </svg>
  )
}

export function SecurityBlock({ level, userLevel, title, variant = 'block', children }: SecurityBlockProps) {
  const required = parseInt(level[1])
  const userMax = userLevel

  if (userMax >= required) {
    return <>{children}</>
  }

  const theme = levelTheme[level]
  const roleLabel = levelRoleName(required)

  // ── 内联条样式（卡片内部、小区块） ─────────────────────────────────────
  if (variant === 'compact') {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', flex: 1 }}>
        <div
          style={{
            background: theme.bg,
            border: `1px solid ${theme.border}`,
            borderRadius: 8,
            padding: '14px 24px',
            textAlign: 'center',
            color: theme.text,
            fontSize: 13,
            width: '100%',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, marginBottom: 10, flexWrap: 'wrap' as const }}>
            <LockIcon size={14} color={theme.text} />
            <span>
              此模块包含 {level} 机密数据，需要 <strong>{roleLabel}</strong> 及以上权限查看
            </span>
          </div>
          <a
            href={getPermissionApplyUrl()}
            target="_blank"
            rel="noreferrer"
            style={{
              fontSize: 12,
              padding: '4px 16px',
              border: `1px solid ${theme.text}`,
              borderRadius: 4,
              background: 'transparent',
              color: theme.text,
              cursor: 'pointer',
              textDecoration: 'none',
            }}
          >
            申请权限
          </a>
        </div>
      </div>
    )
  }

  // ── 大区块样式（整块图表替换） ────────────────────────────────────────
  return (
    <div
      style={{
        border: '1px solid #E8E8E3',
        borderRadius: 10,
        padding: '32px 24px',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 14,
        background: '#F8F8F6',
        minHeight: 160,
        justifyContent: 'center',
        textAlign: 'center',
      }}
    >
      {/* 等级 Badge */}
      <SecurityBadge level={level} confidential />

      {/* 锁图标 */}
      <div
        style={{
          width: 48,
          height: 48,
          borderRadius: '50%',
          background: '#EFEFED',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <LockIcon size={22} color="#999" />
      </div>

      {/* 标题 + 说明 */}
      <div>
        {title && (
          <div style={{ fontSize: 14, fontWeight: 600, color: '#333', marginBottom: 6 }}>
            {title}
          </div>
        )}
        <div style={{ fontSize: 13, color: '#888', lineHeight: 1.6 }}>
          此模块包含 {level} 机密数据，需要 <strong style={{ color: '#555' }}>{roleLabel}</strong> 及以上权限查看
        </div>
      </div>

      {/* 申请权限按钮 */}
      <a
        href={getPermissionApplyUrl()}
        target="_blank"
        rel="noreferrer"
        style={{
          fontSize: 13,
          padding: '6px 20px',
          border: '1px solid #1210B8',
          borderRadius: 6,
          background: 'transparent',
          color: '#1210B8',
          cursor: 'pointer',
          textDecoration: 'none',
        }}
      >
        申请权限
      </a>
    </div>
  )
}
